import { create } from 'zustand';
import { ApiError } from '@app/utils/ApiError';

export type ApiStatus =
  | 'unknown'  // nothing has been attempted yet
  | 'online'   // the last call reached the service
  | 'offline'; // the service is unreachable

interface ApiHealthStore {
  status: ApiStatus;
  /** Message from the failure that took us offline, for the banner detail line. */
  lastError: string | null;
  /** Consecutive outage reports — drives the retry backoff. */
  failureCount: number;
  /** True while a liveness check is in flight. */
  isProbing: boolean;
  /**
   * Bumped when connectivity is regained or the user explicitly asks to retry.
   * Views watch this to re-run loads that failed while the service was down.
   */
  retryToken: number;

  reportSuccess: () => void;
  reportFailure: (error: ApiError) => void;
  setProbing: (isProbing: boolean) => void;
  requestRetry: () => void;
}

/**
 * Tracks whether the backend is reachable. Fed by every call that goes through
 * fetchData, so the UI never has to guess: one banner, one source of truth.
 */
export const useApiHealthStore = create<ApiHealthStore>((set, get) => ({
  status: 'unknown',
  lastError: null,
  failureCount: 0,
  isProbing: false,
  retryToken: 0,

  reportSuccess: () => {
    // Hot path — every successful response lands here. Bail before touching
    // state so we don't re-render the whole tree on each call.
    if (get().status === 'online') return;

    const recovered = get().status === 'offline';
    set((state) => ({
      status: 'online',
      lastError: null,
      failureCount: 0,
      isProbing: false,
      // Only nudge views when we actually came back from an outage; a bump on
      // the very first call of the session would just duplicate their initial load.
      retryToken: recovered ? state.retryToken + 1 : state.retryToken
    }));
  },

  reportFailure: (error) => {
    // A rejected request is not an outage. Those belong to the view that made them.
    if (!error.isOutage) return;

    set((state) => ({
      status: 'offline',
      lastError: error.message,
      failureCount: state.failureCount + 1,
      isProbing: false
    }));
  },

  setProbing: (isProbing) => set({ isProbing }),

  requestRetry: () => set((state) => ({ retryToken: state.retryToken + 1 }))
}));
